import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Input, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import type { UserDelegationFindInterface } from '@zaoblako/shared/modules/security/dtos/user-delegation/user-delegation-find.dto';
import {
  UserDelegationFormDto,
  type UserDelegationFormInterface
} from '@zaoblako/shared/modules/security/dtos/user-delegation/user-delegation-form.dto';
import type { UserFindInterface } from '@zaoblako/shared/modules/security/dtos/user/user-find.dto';
import { UserDelegationTargetKindEnum } from '@zaoblako/shared/modules/security/enums/user-delegation-target-kind.enum';
import { UserDelegationTargetTypeEnum } from '@zaoblako/shared/modules/security/enums/user-delegation-target-type.enum';
import { ConfirmDialogService } from '@zaoblako/web/core/services/confirm-dialog.service';
import { ErrorService } from '@zaoblako/web/core/services/error.service';
import { UserDelegationService } from '@zaoblako/web/core/services/security/user-delegation.service';
import { FormType } from '@zaoblako/web/shared/components/form-sidebar/models/form-state';
import { FormStoreService } from '@zaoblako/web/shared/components/form-sidebar/services/form-store.service';
import { BaseFormComponent } from '@zaoblako/web/shared/dumbs/base-form/base-form.component';
import { SingleSelectComponent } from '@zaoblako/web/shared/dumbs/dropdowns/single-select/single-select.component';
import { UserSingleSelectComponent } from '@zaoblako/web/shared/dumbs/dropdowns/user-single-select/user-single-select.component';
import { SpinnerComponent } from '@zaoblako/web/shared/dumbs/spinner/spinner.component';
import { JoinPipe } from '@zaoblako/web/shared/pipes/join.pipe';
import { UserDelegationTargetKindNamePipe } from '@zaoblako/web/shared/pipes/user-delegation-target-kind-name.pipe';
import { UserDelegationTargetTypeNamePipe } from '@zaoblako/web/shared/pipes/user-delegation-target-type-name.pipe';
import { ButtonComponent } from '@zaoblako/web/shared/dumbs/button/button.component';
import { CardModule } from 'primeng/card';
import { CheckboxModule } from 'primeng/checkbox';
import { Observable } from 'rxjs';

@Component({
  selector: 'crm-user-delegation-form',
  standalone: true,
  imports: [
    CommonModule,
    CardModule,
    SingleSelectComponent,
    JoinPipe,
    UserDelegationTargetKindNamePipe,
    UserDelegationTargetTypeNamePipe,
    UserSingleSelectComponent,
    SpinnerComponent,
    ButtonComponent,
    FormsModule,
    CheckboxModule
  ],
  templateUrl: './user-delegation-form.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class UserDelegationFormComponent extends BaseFormComponent<UserDelegationFormInterface> implements OnInit {
  @Input() public readonly index: number;
  public delegation: UserDelegationFormInterface = <UserDelegationFormInterface>{};

  public form$: Observable<FormType>;
  public loading$: Observable<boolean>;

  public dto = UserDelegationFormDto;

  public readonly kinds: UserDelegationTargetKindEnum[] = Object.values(UserDelegationTargetKindEnum);
  public readonly types: UserDelegationTargetTypeEnum[] = Object.values(UserDelegationTargetTypeEnum);

  public get toUser(): UserFindInterface {
    return this.delegation.toUser as UserFindInterface;
  }

  public set toUser(user: UserFindInterface) {
    this.delegation.toUser = user;
  }

  public constructor(
    private readonly userDelegationService: UserDelegationService,
    private readonly formStoreService: FormStoreService,
    private readonly confirmService: ConfirmDialogService,
    private readonly errorService: ErrorService,
    private readonly cdRef: ChangeDetectorRef
  ) {
    super();
  }

  public ngOnInit(): void {
    this.form$ = this.formStoreService.select((state) => state.forms[this.index]);
    this.loading$ = this.formStoreService.select((state) => state.forms[this.index]?.loading);

    if (this.formStoreService.getState.forms[this.index]?.isEdit) {
      this.getById();
    } else {
      this.delegation.fromUser = this.formStoreService.getState.forms[this.index].data?.user;
      if (this.kinds?.length === 1) {
        this.delegation.kind = this.kinds[0];
      }
    }
  }

  public getById() {
    this.formStoreService.setLoading(true, this.index);

    this.userDelegationService
      .findById<UserDelegationFindInterface>(this.formStoreService.getState.forms[this.index]?.entityId)
      .subscribe({
        next: (delegation) => {
          this.delegation = delegation;
          this.formStoreService.setLoading(false, this.index);
          this.cdRef.markForCheck();
        }
      });
  }

  public saveDelegation() {
    if (this.checkValidate(this.delegation).valid) {
      if (this.formStoreService.getState.forms[this.index]?.entityId) {
        this.formStoreService.setLoading(true, this.index);

        this.userDelegationService
          .update<UserDelegationFormInterface, UserDelegationFindInterface>(this.delegation)
          .subscribe({
            next: (delegation) => {
              this.formStoreService.setLoading(false, this.index);
              this.errorService.addSuccessMessage(
                `Делегирование с пользователем "${delegation?.toUser?.profile?.name}" обновлено!`
              );
              this.formStoreService.saveNext(delegation, this.index);
            },
            error: () => {
              this.formStoreService.setLoading(false, this.index);
            }
          });
      } else {
        this.formStoreService.setLoading(true, this.index);

        this.userDelegationService.create<UserDelegationFindInterface>(this.delegation).subscribe({
          next: (delegation) => {
            this.formStoreService.setLoading(false, this.index);
            this.errorService.addSuccessMessage('Делегирование создано!');
            this.formStoreService.saveNext(delegation, this.index);
          },
          error: () => {
            this.formStoreService.setLoading(false, this.index);
          }
        });
      }
    }
  }

  public deleteDelegation() {
    this.confirmService.confirm({
      message: `Вы действительно хотите удалить делегирование с пользователем "${this.delegation?.toUser?.profile?.name}"?`,
      accept: () => {
        this.formStoreService.setLoading(true, this.index);
        this.cdRef.markForCheck();

        this.userDelegationService
          .deleteById(this.formStoreService.getState.forms[this.index]?.entityId)
          .subscribe(() => {
            this.errorService.addSuccessMessage('Делегирование удалено!');
            this.formStoreService.deleteNext(this.index);
          });
      }
    });
  }
}
